import dayjs from 'dayjs';
import dayjsPtBR from 'dayjs/locale/pt-br';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import utcPlugin from 'dayjs/plugin/utc';
import StringMask from 'string-mask';
import BaseHelpers from '@/plugins/baseHelpers';

let instance = null;

class BaseFormat {
  constructor () {
    dayjs.
      extend(relativeTime,).
      extend(customParseFormat,).
      extend(isSameOrAfter,).
      extend(isSameOrBefore,).
      extend(utcPlugin,).
      locale(dayjsPtBR,);
  }

  date = (...params) => {
    return dayjs(...params,);
  };

  utcDate = (...params) => {
    return dayjs(...params,).utc();
  };

  dateFromNow = (dateTarget,) => {
    if (dateTarget) { return dayjs(dateTarget,).calendar(); }
    return '-';
  };

  prefixedNumber = (value,) => {
    const ranges = [
      { divider: 1e3, suffix: 'k', },
      { divider: 1e6, suffix: 'M', },
      { divider: 1e9, suffix: 'G', },
      { divider: 1e12, suffix: 'T', },
      { divider: 1e15, suffix: 'P', },
      { divider: 1e18, suffix: 'E', },
    ];
    let prefixedValue = value;

    ranges.forEach((range,) => {
      if (value >= range.divider) {
        prefixedValue = `${(value / range.divider).toString()}${range.suffix}`;
      }
    },);
    return prefixedValue.toString();
  };

  percentage = (value,) => {
    return `${this.float(value,)}%`;
  };

  float = (value,) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },).format(value,);
  };

  floatUS = (value,) => {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },).format(value,);
  };

  integer = (value,) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'decimal',
      minimumIntegerDigits: 1,
    },).format(value,);
  };

  money = (value,) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    },).format(value,);
  };

  phone = (value,) => {
    if (value) {
      const phone = new StringMask('(00) 0000-0000',);
      const phone9 = new StringMask('(00) 9 0000-0000',);
      const cleanPhone = this.cleanPhone(value,);
      const localPhone = cleanPhone.length > 11 && cleanPhone.startsWith('55',)
        ? cleanPhone.slice(2,)
        : cleanPhone;

      if (!localPhone) {
        return value;
      }

      if (localPhone.length <= 10) {
        value = phone.apply(localPhone,);
      } else {
        value = phone9.apply(localPhone,);
      }
    }

    return value;
  };

  cnpj = (value,) => {
    if (value) {
      const cnpj = new StringMask('AA.AAA.AAA/AAAA-00',);

      value = cnpj.apply(value,);
    }

    return value;
  };

  cpf = (value,) => {
    let ao_cpf = '';
    if (value) {
      ao_cpf = value;
      ao_cpf = ao_cpf.replace(/(\d{3})(\d)/, '$1.$2',); //Coloca um ponto entre o terceiro e o quarto dígitos
      ao_cpf = ao_cpf.replace(/(\d{3})(\d)/, '$1.$2',); //Coloca um ponto entre o terceiro e o quarto dígitos
      //de novo (para o segundo bloco de números)
      ao_cpf = ao_cpf.replace(/(\d{3})(\d{1,2})$/, '$1-$2',); //Coloca um hífen entre o terceiro e o quarto dígitos
    }

    return ao_cpf || value;
  };

  /* Logradouro, Número - Complemento - Bairro, Cidade - Estado, CEP, País */
  address = (address,) => {
    if (address) {
      const query = [
        address.street,
        [address.number, address.complement, address.neighborhood,],
        [address.city, address.state,],
        this.zipcode(address.zipcode,),
        address.country,
      ];

      return query.reduce((result, part,) => {
        if (BaseHelpers._toRawType(part,) === 'Array') {
          const dashSeparatedValues = part.filter((value,) => value,).join` - `;
          dashSeparatedValues && result.push(dashSeparatedValues,);
        } else if (part) {
          result.push(part,);
        }

        return result;
      }, [],).join`, `;
    }
  };

  city_state = ({ city, state, },) => `${city || ''} - ${state || ''}`;

  zipcode = (value,) => {
    let result;
    if (value) {
      const zipcode = new StringMask('00000-000',);

      result = zipcode.apply(value,) || value;
    }

    return result;
  };

  bankAccount = (numbers, checkDigit,) => {
    if (numbers) {
      const checkDigitString = checkDigit ? `-${checkDigit}` : '';
      return `${numbers}${checkDigitString}`;
    }
  };

  whitespaceToUnderscore = (value,) => {
    if (!value) {
      return value;
    }
    return value.replace(/\s+/gi, '_',);
  };

  // Used for Zipcode, Mei, CNPJ, etc.
  cleanSymbols = (value,) => {
    if (!value) { return value; }
    return value.replace(/[^\w\s]/gi, '',);
  };

  cleanPhone = (value,) => {
    if (!value) { return value; }
    return value.replace(/[^0-9]/gi, '',);
  };

  cleanRenavam = (value,) => {
    if (!value) { return value; }
    return value.replace(/[^0-9]+/g, '',);
  };

  cleanCpf = (value,) => {
    if (!value) { return value; }
    return value.replace(/\.|-/g, '',);
  };

  sortString = (sort, sortBy,) => {
    const arraySorts = Array.isArray(sort,) && Array.isArray(sortBy,);
    if (!arraySorts && (sort && sortBy)) { return `${sort}${sortBy}`; }

    const sortStrings = [];
    if ((sort?.length && sortBy?.length) &&
      sort?.length === sortBy?.length) {

      sort.forEach((sortItem, index,) => {
        sortStrings.push(`${sortItem}${sortBy[index]}`,);
      },);

    }
    return sortStrings;
  };

  removeStringAccents = (string,) => {
    const accentRegExp = /[\u0300-\u036f]/g;
    return string.normalize('NFD',).replace(accentRegExp, '',);
  };

  pix = ({key, value,},) => {
    switch (key) {
    case 'CPF':
      return this.cpf(value,);
    case 'CNPJ':
      return this.cnpj(value,);
    case 'PHONE':
      return this.phone(value,);
    default:
      return value;
    }
  };

  install (Vue,) {
    //Install for VueJS Plugin
    console.log('[BASE_FORMAT] Install',);

    Vue.prototype.$format = {
      phone: this.phone,
      date: this.date,
      money: this.money,
      dateFromNow: this.dateFromNow,
      prefixedNumber: this.prefixedNumber,
      percentage: this.percentage,
      float: this.float,
      floatUS: this.floatUS,
      integer: this.integer,
      cnpj: this.cnpj,
      cpf: this.cpf,
      bankAccount: this.bankAccount,
      whitespaceToUnderscore: this.whitespaceToUnderscore,
      cleanSymbols: this.cleanSymbols,
      cleanPhone: this.cleanPhone,
      cleanCpf: this.cleanCpf,
      cleanRenavam: this.cleanRenavam,
      address: this.address,
      city_state: this.city_state,
      zipcode: this.zipcode,
      removeStringAccents: this.removeStringAccents,
      pix: this.pix,
    };
  }
}

export default (() => {
  if (!instance) { instance = new BaseFormat(); }

  return instance;
})();
